import React from "react";
import { Box, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import CustomSkeleton from "./CustomSkeleton";
import CustomPanel from "./CustomPanel";
import { Center, Stack } from "../layout/reusableLayouts";

interface IdNotFoundProps {
  title: string;
  isLoading: boolean;
  showSkeleton?: boolean;
  notFoundDelayMs?: number;
}

const getSkeletonVariant = (title: string) => {
  switch (title.toLowerCase()) {
    case "card":
      return "card";
    case "user":
      return "user";
    default:
      return "list";
  }
};

const IdNotFoundPage: React.FC<IdNotFoundProps> = ({
  title,
  isLoading,
  showSkeleton,
  notFoundDelayMs = 400, // 400ms default delay
}) => {
  const skeletonVariant = getSkeletonVariant(title);
  const [showNotFound, setShowNotFound] = React.useState(false);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (!isLoading) {
      timeout = setTimeout(() => setShowNotFound(true), notFoundDelayMs);
    } else {
      setShowNotFound(false);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading, notFoundDelayMs]);

  if (isLoading && showSkeleton) {
    return (
      <Box sx={{ margin: "var(--space-6)" }}>
        <CustomPanel>
          <Box sx={{ padding: "var(--space-5)" }}>
            <CustomSkeleton variant={skeletonVariant} />
          </Box>
        </CustomPanel>
      </Box>
    );
  }

  if (isLoading && !showSkeleton) {
    return (
      <Center sx={{ height: "100%", padding: "var(--space-9)" }}>
        <Typography variant="h5">Loading {title.toLowerCase()}...</Typography>
      </Center>
    );
  }

  if (!showNotFound) {
    return null;
  }

  return (
    <Center sx={{ height: "100%", padding: "var(--space-9)" }}>
      <Stack gap="var(--space-4)" sx={{ alignItems: "center" }}>
        <ErrorOutlineIcon
          sx={{ fontSize: 80, color: "var(--color-text-danger)" }}
        />
        <Typography variant="h5">{title} not found</Typography>
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          We couldn't find the {title.toLowerCase()} you're looking for. Please
          check the ID and try again.
        </Typography>
      </Stack>
    </Center>
  );
};

export default IdNotFoundPage;
